import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import "./ContactForm.css";
import { TextField, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

const RATE_LIMIT_MS = 10 * 60 * 1000; // 10 minutes between submissions
const MIN_FORM_TIME_MS = 3000;         // must spend at least 3s on page

type Status = "idle" | "success" | "error" | "rate-limited";

const ContactForm = () => {
  const form = useRef<HTMLFormElement>(null);
  const loadTime = useRef(Date.now());
  const { t } = useTranslation();

  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status>("idle");

  const isRateLimited = (): boolean => {
    const last = localStorage.getItem("contactLastSubmit");
    return !!last && Date.now() - parseInt(last) < RATE_LIMIT_MS;
  };

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();

    if (honeypot) return;
    if (Date.now() - loadTime.current < MIN_FORM_TIME_MS) return;

    if (isRateLimited()) {
      setStatus("rate-limited");
      return;
    }

    if (!form.current) return;

    setLoading(true);
    setStatus("idle");

    emailjs
      .sendForm("service_znzurw7", "template_gea5qah", form.current, {
        publicKey: "ABSW80a6M1UnFNvvn",
      })
      .then(() => {
        setStatus("success");
        localStorage.setItem("contactLastSubmit", Date.now().toString());
        form.current?.reset();
      })
      .catch(() => {
        setStatus("error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,207,157,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,207,157,0.65)" },
      "&.Mui-focused fieldset": { borderColor: "#FFCF9D", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255,207,157,0.7)",
      "&.Mui-focused": { color: "#FFCF9D" },
    },
    "& .MuiOutlinedInput-input": { color: "#fff" },
    "& .MuiOutlinedInput-inputMultiline": { color: "#fff" },
  };

  return (
    <div className="cf-container">
      <form className="cf-form" ref={form} onSubmit={sendEmail}>
        {/* Honeypot – invisible to humans, bots fill it in */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
        />

        {status === "success" && (
          <div className="cf-alert cf-alert--success">
            {t("contactForm.success", "Message sent! We'll get back to you soon.")}
          </div>
        )}
        {status === "error" && (
          <div className="cf-alert cf-alert--error">
            {t("contactForm.error", "Something went wrong. Please try again.")}
          </div>
        )}
        {status === "rate-limited" && (
          <div className="cf-alert cf-alert--error">
            {t("contactForm.rateLimited", "Please wait a few minutes before sending another message.")}
          </div>
        )}

        <TextField
          required
          label={t("contactForm.name")}
          name="name"
          variant="outlined"
          sx={inputSx}
        />
        <TextField
          required
          label={t("contactForm.email")}
          name="email"
          type="email"
          variant="outlined"
          sx={inputSx}
        />
        <TextField
          required
          label={t("contactForm.subject")}
          name="title"
          variant="outlined"
          sx={inputSx}
        />
        <TextField
          label={t("contactForm.message")}
          name="message"
          multiline
          rows={4}
          variant="outlined"
          sx={inputSx}
        />

        <Button
          type="submit"
          variant="outlined"
          disabled={loading || status === "success"}
          className="cf-btn"
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#FFCF9D" }} />
          ) : (
            t("contactForm.send")
          )}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;
